import { z } from 'zod';
import { htmlAsPlainText } from 'src/v2/shared/domain/richHtmlFallback';

/**
 * Helper function to check if HTML content is empty (strips tags and checks for actual text)
 * Used for validating rich text editor fields
 *
 * @param html - The HTML content to check
 * @param options.allowImageOnly - When true, HTML containing an `<img>`
 *   tag counts as content even with no text. Opt-in per caller: the
 *   task-description Rich Text Editor lets auditors submit a
 *   description that is ONLY an inline image (e.g. a screenshot of the
 *   violation), but text-required fields (question text, instructions,
 *   criteria) keep the strict text check.
 */
export const isHtmlContentEmpty = (
  html: string,
  { allowImageOnly = false }: { allowImageOnly?: boolean } = {},
): boolean => {
  if (!html || html.trim() === '') return true;

  if (allowImageOnly && /<img\s/i.test(html)) return false;

  // Read the wording out the way a reader would see it. `htmlAsPlainText` owns tag removal
  // and entity resolving for the whole app, so this check cannot drift from what the
  // question and task surfaces display.
  return htmlAsPlainText(html) === '';
};

/**
 * Zod schema for media response object
 */
const mediaResponseSchema = z.object({
  media: z.object({
    mediaId: z.string(),
    entityType: z.string().optional(),
    s3Key: z.string().optional(),
    fileName: z.string().optional(),
    extension: z.string().optional(),
    fileSize: z.number().optional(),
    program: z.string().optional(),
    businessUnit: z.string().optional(),
    createdAt: z.string().optional(),
    createdBy: z.string().optional(),
    lastUpdatedAt: z.string().optional(),
    lastUpdatedBy: z.string().optional(),
  }),
  presignedUrl: z.string(),
});

/**
 * Zod schema for media responses map (field-level media)
 */
const mediaResponsesMapSchema = z.object({
  instruction: z.array(mediaResponseSchema).optional(),
  taskDescription: z.array(mediaResponseSchema).optional(),
});

/**
 * Zod schema for task details
 * Both taskName and taskDescription are required when task is present
 */
const taskDetailsSchema = z
  .object({
    taskName: z.string().optional(),
    taskDescription: z.string().optional(),
    taskId: z.string().optional(),
  })
  .optional()
  .nullable()
  .superRefine((data, ctx) => {
    // Only validate if taskDetails object exists and has some content
    if (!data) return;

    const hasTaskName = data.taskName && data.taskName.trim().length > 0;
    const hasTaskDescription =
      data.taskDescription && !isHtmlContentEmpty(data.taskDescription);

    // If either field has content, both are required
    if (hasTaskName || hasTaskDescription) {
      if (!hasTaskName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Task name is required',
          path: ['taskName'],
        });
      }
      if (!hasTaskDescription) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Task description is required',
          path: ['taskDescription'],
        });
      }
    }
  });

/**
 * Base schema for subquestion
 */
const subQuestionSchema = z.object({
  subQuestionId: z.string().optional(),
  title: z.string().min(1, 'Sub-question title is required'),
  instruction: z
    .string()
    .refine((val) => !isHtmlContentEmpty(val), 'Sub-question instruction is required'),
  taskDetails: taskDetailsSchema,
  mediaResponses: z.array(mediaResponseSchema).optional(),
  mediaResponsesMap: mediaResponsesMapSchema.optional(),
});

/**
 * Zod schema for translation form validation
 * Validates all required fields for question translation editing
 * Task fields at question level are conditionally required (when task exists)
 * SubQuestions array is optional
 */
export const translationFormSchema = z
  .object({
    title: z.string().min(1, 'Title is required'),
    questionText: z
      .string()
      .refine((val) => !isHtmlContentEmpty(val), 'Question text is required'),
    auditInstruction: z
      .string()
      .refine((val) => !isHtmlContentEmpty(val), 'Audit instruction is required'),
    passCriteria: z
      .string()
      .refine((val) => !isHtmlContentEmpty(val), 'Pass criteria is required'),
    failCriteria: z
      .string()
      .refine((val) => !isHtmlContentEmpty(val), 'Fail criteria is required'),
    justification: z
      .string()
      .refine((val) => !isHtmlContentEmpty(val), 'Justification is required'),
    // Task fields - validated conditionally via superRefine
    taskName: z.string().optional(),
    taskDescription: z.string().optional(),
    // Flag to indicate if task section exists (set by form)
    hasTask: z.boolean().optional(),
    // SubQuestions array is optional
    subQuestions: z.array(subQuestionSchema).optional(),
  })
  .superRefine((data, ctx) => {
    // Only validate task fields if hasTask flag is true
    if (data.hasTask) {
      const hasTaskName = data.taskName && data.taskName.trim().length > 0;
      const hasTaskDescription =
        data.taskDescription && !isHtmlContentEmpty(data.taskDescription);

      if (!hasTaskName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Task name is required',
          path: ['taskName'],
        });
      }
      if (!hasTaskDescription) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Task description is required',
          path: ['taskDescription'],
        });
      }
    }
  });

/**
 * TypeScript type inferred from the Zod schema
 * Use this type for form data throughout the application
 */
export type TranslationFormData = z.infer<typeof translationFormSchema>;
